import { defineStore } from 'pinia'
import type { LoginRequest, LoginResponse, UserInfo } from '~/types/auth'
import { useMenuStore } from '~/stores/menu'
import { useTabStore } from '~/stores/tab'
import { useNotificationStore } from '~/stores/notification'

const COOKIE_NAME = 'refreshToken'
const COOKIE_MAX_AGE = 60 * 60 * 24 * 30 // 30일

// 진행 중인 refresh 요청을 공유하기 위한 single-flight 가드.
// F5 새로고침 시 auth.client.ts(app:mounted)와 api.ts interceptor(401)가
// 거의 동시에 refresh()를 호출하는데, 백엔드가 refresh token rotation
// (기존 토큰 revoke + 새 토큰 발급)을 수행하므로 두 번째 호출은 이미 폐기된
// 토큰으로 요청해 실패 → 세션 만료로 이어진다.
// 진행 중인 Promise를 재사용하면 refresh 요청이 한 번만 나가고
// 모든 호출자가 동일한 결과(새 accessToken)를 공유한다.
// client 에서만 호출되므로 module-level 변수로 두어도 SSR 요청 간 오염이 없다.
let refreshInFlight: Promise<void> | null = null

// 세션 만료 복구(인-페이지 모달) 조정용 resolver.
// api.ts interceptor(Vue 컨텍스트 밖)가 requestSessionRecovery()로 모달을 띄우고
// 그 Promise를 await 한다. SessionExpiredModal.vue 가 로그인 성공 시
// resolveSessionRecovery(token), 로그아웃 시 cancelSessionRecovery()를 호출해 깨운다.
// client 에서만 동작하므로 module-level 변수로 둔다(refreshInFlight 와 동일 이유).
let sessionRecoveryResolve: ((token: string) => void) | null = null
let sessionRecoveryReject: ((reason?: unknown) => void) | null = null

export const useAuthStore = defineStore('auth', {
  state: () => ({
    accessToken:      null as string | null,
    refreshToken:     null as string | null,
    user:             null as UserInfo | null,
    sessionRecovering: false as boolean,
  }),

  getters: {
    isAuthenticated: (state) => !!state.accessToken,
    currentUser:     (state) => state.user,
  },

  actions: {
    async login(credentials: LoginRequest) {
      const { $api } = useNuxtApp()
      const response = await $api.post<{ data: LoginResponse }>('/api/v1/auth/login', credentials)
      const data = response.data.data

      this.accessToken  = data.accessToken
      this.refreshToken = data.refreshToken

      // localStorage 대신 쿠키에 저장 → 서버(SSR)에서도 읽을 수 있어 hydration mismatch 방지
      const cookie = useCookie(COOKIE_NAME, { maxAge: COOKIE_MAX_AGE, path: '/', sameSite: 'lax' })
      cookie.value = data.refreshToken

      await this.fetchMe()
    },

    async refresh() {
      // 이미 진행 중인 refresh가 있으면 그 Promise를 그대로 반환한다(single-flight).
      // 동시 호출이 각자 백엔드에 요청해 토큰 rotation race를 일으키는 것을 막는다.
      if (refreshInFlight) {
        return refreshInFlight
      }

      refreshInFlight = (async () => {
        const { $api } = useNuxtApp()

        // Pinia 상태에서 읽음 (initFromStorage() 또는 login() 이후 설정됨)
        // useCookie()를 비동기 액션 내부에서 직접 호출하면 Nuxt 컨텍스트가
        // 불안정해질 수 있으므로 store 상태를 사용한다.
        const storedRefreshToken = this.refreshToken
        if (!storedRefreshToken) {
          throw new Error('No refresh token')
        }

        const response = await $api.post<{ data: LoginResponse }>('/api/v1/auth/refresh', {
          refreshToken: storedRefreshToken,
        })

        const data = response.data.data
        this.accessToken  = data.accessToken
        this.refreshToken = data.refreshToken

        // 갱신된 refreshToken을 쿠키에 업데이트
        const newCookie = useCookie(COOKIE_NAME, { maxAge: COOKIE_MAX_AGE, path: '/', sameSite: 'lax' })
        newCookie.value = data.refreshToken
      })()

      try {
        await refreshInFlight
      } finally {
        refreshInFlight = null
      }
    },

    async fetchMe() {
      const { $api } = useNuxtApp()
      const response = await $api.get<{ data: UserInfo }>('/api/v1/auth/me')
      this.user = response.data.data
    },

    async logout() {
      try {
        const { $api } = useNuxtApp()
        await $api.post('/api/v1/auth/logout')
      } catch {
        // ignore
      } finally {
        this.accessToken  = null
        this.refreshToken = null
        this.user = null

        useMenuStore().reset()
        useTabStore().reset()
        useNotificationStore().reset()

        const cookie = useCookie(COOKIE_NAME)
        cookie.value = null
      }
    },

    // ── 세션 만료 인-페이지 모달 복구 ──────────────────────────────────────
    // interceptor 가 호출: 모달을 띄우고(sessionRecovering=true) 사용자의
    // 재로그인/로그아웃 결정을 기다린다. 성공 시 새 accessToken 으로 resolve.
    requestSessionRecovery(): Promise<string> {
      // 이미 복구 진행 중이면 직전 대기를 취소하고 새 대기로 교체한다
      // (api.ts 의 isRefreshing 가드로 보통 단일 진입이지만 방어적으로 처리).
      sessionRecoveryReject?.(new Error('SESSION_RECOVERY_SUPERSEDED'))
      this.sessionRecovering = true
      return new Promise<string>((resolve, reject) => {
        sessionRecoveryResolve = resolve
        sessionRecoveryReject = reject
      })
    },

    // 모달에서 로그인 성공 시 호출: 대기 중인 interceptor 를 새 토큰으로 깨운다.
    resolveSessionRecovery(token: string) {
      this.sessionRecovering = false
      sessionRecoveryResolve?.(token)
      sessionRecoveryResolve = null
      sessionRecoveryReject = null
    },

    // 모달에서 로그아웃/취소 시 호출: 대기 중인 interceptor 를 reject 해
    // /login 으로 보내는 경로로 진행시킨다.
    cancelSessionRecovery() {
      this.sessionRecovering = false
      sessionRecoveryReject?.(new Error('SESSION_RECOVERY_CANCELLED'))
      sessionRecoveryResolve = null
      sessionRecoveryReject = null
    },

    initFromStorage() {
      // 쿠키에서 읽음 — 서버·클라이언트 모두 동일하게 동작
      const cookie = useCookie(COOKIE_NAME)
      if (cookie.value) {
        this.refreshToken = cookie.value
      }
    },
  },
})
